<template>
  <div v-if="!loading" class="loading-container">
    <div v-if="!requiresDocument" class="completion-state">
      <div class="completion-card">
        <div class="completion-icon">✓</div>
        <div class="completion-content">
          <h1>Entrega Completada</h1>
          <p>
            Todos los artículos de la venta (Folio: {{ foil }}) ya fueron entregados físicamente en mostrador.
            No se requiere generar un vale de despacho.
          </p>
        </div>
      </div>
    </div>
    <div v-else>
      <div class="print-preview-wrapper">
        <!-- Barra de Acciones (No imprimible) -->
        <div class="no-print action-bar">
          <button class="btn-print" @click="imprimirDocumento">
            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none">
              <polyline points="6 9 6 2 18 2 18 9"></polyline>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
              <rect x="6" y="14" width="12" height="8"></rect>
            </svg>
            Imprimir Vale de Resguardo
          </button>
          <v-select
            v-if="pickupStoreOptions.length > 0 && !hasRoutePickupStoreSelection && !hasRouteAllSelection"
            v-model="selectedPickupStoreKey"
            :items="pickupStoreOptions"
            item-text="label"
            item-value="key"
            label="Sucursal de recolección"
            outlined
            dense
            hide-details
            class="pickup-store-select"
          ></v-select>
        </div>
        <!-- Hoja Tamaño Carta -->
        <div class="hoja-carta">
          <!-- 1. CABECERA CORPORATIVA -->
          <header class="header-section">
            <div class="header-left">
              <!-- 🔥 INICIO DE SECCIÓN CON LOGO Y TEXTO ALINEADOS -->
              <div class="brand-container">
                <img src="/logo-ma.ico" alt="Logo Mundo Albercas" class="company-logo" />
                <div class="brand-info">
                  <h1 class="logo-title">Mundo Albercas</h1>
                  <p class="company-data">{{ addressLine1 }}</p>
                  <p class="company-data">{{ addressLine2 }}</p>
                  <p class="company-data">{{ addressLine3 }}</p>
                  <p class="company-data">Atención al Cliente: {{ addressLine4 }}</p>
                  <p class="company-data text-muted">Fecha de Venta: {{ createdAt }}</p>
                </div>
              </div>
              <!-- 🔥 FIN DE SECCIÓN -->
            </div>
            <div class="header-right">
              <div class="folio-box">
                <!-- Columna Izquierda: Textos -->
                <div class="folio-text">
                  <div class="folio-title">VALE DE RECOLECCIÓN</div>
                  <div class="folio-number">FOLIO: {{ foil }}</div>
                  <div class="badge-status" v-if="hasPendingItems">PENDIENTE DE ENTREGA</div>
                  <div class="badge-status delivered" v-else>ENTREGADO</div>
                </div>
                <!-- Columna Derecha: QR -->
                <div class="folio-qr">
                  <img src="https://api.qrserver.com/v1/create-qr-code/?size=75x75&data=88745" alt="QR Folio"
                    class="header-qr" />
                </div>
              </div>
              <span class="company-data text-muted">Fecha de impresión: {{ printedAt }}</span>
            </div>
          </header>
          <!-- 2. SECCIÓN DE SEGURIDAD Y CÓDIGO QR -->
          <section class="security-section">
            <div class="alert-box">
              <div class="alert-icon">⚠️</div>
              <div class="alert-text">
                <strong>IMPORTANTE:</strong> Es indispensable presentar <strong>Identificación Oficial Vigente
                  (INE / Pasaporte)</strong> de la persona autorizada al momento de presentarse a recoger la
                mercancía. Sin identificación, no se podrá realizar la entrega.
              </div>
            </div>
          </section>
          <!-- 3. DATOS DEL RESPONSABLE (GRID) -->
          <section class="data-grid">
            <div class="data-card">
              <h3 class="data-title">Titular de la Compra</h3>
              <p class="data-row" v-if="clientId != 'N/A'"><span>Cliente:</span> {{ clientId }}</p>
              <p class="data-row"><span>Nombre:</span> {{ clientName }}</p>
            </div>
            <div class="data-card">
              <h3 class="data-title">Responsable Autorizado para Retiro</h3>
              <p class="data-row"><span>Nombre:</span> {{ authorizedClientName }}</p>
              <p class="data-row"><span>Contacto:</span> {{ authorizedClientContact }}</p>
              <p class="data-row"><span>Email:</span> {{ authorizedClientEmail }}</p>
            </div>
          </section>
          <!-- 4. TABLA DE ARTÍCULOS EN RESGUARDO -->
          <section class="items-section" v-if="hasPendingItems">
            <h3 class="items-title">Productos pendientes de entrega</h3>
            <table class="items-table">
              <thead>
                <tr>
                  <th width="50%">Descripción del Producto</th>
                  <th width="12%">SKU</th>
                  <th v-if="showPickupStoreColumn" width="20%">Sucursal de Entrega</th>
                  <th width="18%" class="text-center th-compact">Cantidad a Entregar</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in groupedItems" :key="item.product.sku">
                  <td>
                    <div class="font-medium">{{ item.product.name }}</div>
                    <div class="product-meta text-muted">{{ item.product.brand_name || 'N/A' }} - {{ item.product.model || 'N/A' }}</div>
                  </td>
                  <td class="sku-cell font-mono text-muted">{{ item.product.barcode || 'N/A' }}</td>
                  <td v-if="showPickupStoreColumn" class="text-muted">{{ item.pickupStoreDisplay || 'N/A' }}</td>
                  <td class="text-center item-qty">{{ item.pending_quantity }} pz</td>
                </tr>
              </tbody>
            </table>
          </section>
          <section class="items-section" v-else>
            <v-alert type="success" border="left" prominent>
              Este vale de despacho ha sido cerrado. Todos los artículos fueron entregados al cliente exitosamente.
            </v-alert>
          </section>
          <!-- 4.1. TABLA DE ARTÍCULOS ENTREGADOS -->
          <section class="items-section" v-if="deliveredGroupedItems.length > 0">
            <h3 class="items-title">Productos entregados</h3>
            <table class="items-table">
              <thead>
                <tr>
                  <th width="45%">Descripción del Producto</th>
                  <th width="15%">SKU</th>
                  <th width="40%" class="text-center th-compact">Entregas Realizadas</th>
                </tr>
              </thead>
              <template v-if="deliveredGroupedItems.length">
                <tbody
                  v-for="item in deliveredGroupedItems"
                  :key="item.key"
                >
                  <tr
                    class="delivered-product-row"
                  >
                    <td>
                      <div class="font-medium">{{ item.product.name }}</div>
                      <div class="product-meta text-muted">{{ item.product.brand_name || 'N/A' }} - {{ item.product.model || 'N/A' }}</div>
                    </td>
                    <td class="sku-cell font-mono text-muted">{{ item.product.barcode || 'N/A' }}</td>
                    <td class="text-center item-qty">{{ item.delivered_quantity }} pz</td>
                  </tr>
                  <tr
                    v-for="delivery in item.deliveries"
                    :key="`${item.key}-${delivery.key}`"
                    class="delivery-detail-row"
                  >
                    <td colspan="2" class="delivery-detail-text">
                      {{ delivery.formatted_date }} - Sucursal {{ delivery.store_name }}
                    </td>
                    <td class="text-center delivery-qty">{{ delivery.quantity }} pz</td>
                  </tr>
                </tbody>
              </template>
              <tbody
                v-else
              >
                <tr>
                  <td colspan="3" class="text-center text-muted">No se han entregado productos aún.</td>
                </tr>
              </tbody>
            </table>
          </section>
          <!-- 5. POLÍTICAS Y SECCIÓN DE FIRMAS -->
          <section class="legal-footer">
            <p class="legal-text">
              La mercancía amparada en este comprobante ha sido pagada en su totalidad y se encuentra en proceso
              de
              traslado desde nuestro Almacén Central hacia la sucursal asignada. El cliente recibirá una
              notificación
              automática por WhatsApp/Correo en cuanto su pedido esté listo. A partir de dicha notificación,
              cuenta
              con un plazo máximo de 30 días naturales para su recolección. Transcurrido este periodo sin ser
              recolectada,
              la mercancía reingresará al inventario disponible y el importe pagado se convertirá íntegramente en
              un Saldo
              a Favor para el cliente, sin cargos ni penalizaciones.
            </p>
            <div class="signatures-area">
              <div class="signature-line">
                <div class="line"></div>
                <p class="signature-title">Firma de Recibido del Cliente</p>
                <p class="signature-subtitle">Nombre y Fecha de Recepción</p>
              </div>
              <div class="signature-line">
                <div class="line"></div>
                <p class="signature-title">Sello de Tienda / Empleado</p>
                <p class="signature-subtitle">Autorización de Salida</p>
              </div>
            </div>
          </section>
          <footer class="document-footer">
            footer footer footer footer footer footer
          </footer>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ClientDeliveryService from '@/services/ClientDeliveryService';
import formatDate from '@/utils/DateUtils';

export default {
  name: 'ValeResguardoPreview',
  data() {
    return {
      loading: true,
      defaultTicketData: {
        foil: 'N/A',
        created_at: null,
        client: {
          name: 'N/A',
          id: 'N/A',
        },
        authorized_client_name: 'N/A',
        authorized_client_contact: 'N/A',
        authorized_client_email: 'N/A',
        pickup_store: {
          name: 'N/A',
        },
        items: [],
      },
      ticketData: this.defaultTicketData,
      selectedPickupStoreKey: null,
    };
  },
  methods: {
    closeWindow() {
      window.close();
    },
    formatDate(dateValue) {
      if (!dateValue) return 'N/A';
      return formatDate(dateValue);
    },
    splitAddress(address) {
      if (!address) return [];

      return String(address)
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part.length > 0);
    },
    imprimirDocumento() {
      window.print();
    },
    getPickupStoreKey(pickupStore) {
      if (!pickupStore) return null;

      if (pickupStore.id !== undefined && pickupStore.id !== null && pickupStore.id !== '') {
        return `id-${pickupStore.id}`;
      }

      if (
        pickupStore.store_id !== undefined
        && pickupStore.store_id !== null
        && pickupStore.store_id !== ''
      ) {
        return `store-id-${pickupStore.store_id}`;
      }

      const name = String(pickupStore.name || '').trim().toLowerCase();
      const address = String(pickupStore.address || '').trim().toLowerCase();

      if (!name && !address) return null;

      return `${name}|${address}`;
    },
    getPickupStoreLabel(pickupStore) {
      if (!pickupStore) return 'N/A';

      const name = String(pickupStore.name || '').trim();
      const address = String(pickupStore.address || '').trim();

      if (name) return name;
      if (address) return address;
      return 'N/A';
    },
    normalizeStoreId(value) {
      if (value === undefined || value === null || value === '') return null;
      return String(value).trim();
    },
    getPickupStoreIdValue(pickupStore) {
      if (!pickupStore) return null;

      if (pickupStore.id !== undefined && pickupStore.id !== null && pickupStore.id !== '') {
        return this.normalizeStoreId(pickupStore.id);
      }

      if (
        pickupStore.store_id !== undefined
        && pickupStore.store_id !== null
        && pickupStore.store_id !== ''
      ) {
        return this.normalizeStoreId(pickupStore.store_id);
      }

      return null;
    },
    getPickupStoreKeyFromRouteStoreId(routeStoreId) {
      const normalizedRouteStoreId = this.normalizeStoreId(routeStoreId);
      if (!normalizedRouteStoreId) return null;

      const matchedOption = this.pickupStoreOptions.find((storeOption) => {
        const optionStoreId = this.getPickupStoreIdValue(storeOption.store);
        return optionStoreId === normalizedRouteStoreId;
      });

      return matchedOption ? matchedOption.key : null;
    },
    ensureSelectedPickupStore() {
      if (!this.pickupStoreOptions.length) {
        this.selectedPickupStoreKey = null;
        return;
      }

      if (this.hasRouteAllSelection) {
        this.selectedPickupStoreKey = 'all';
        return;
      }

      if (this.hasRoutePickupStoreSelection) {
        const routeSelectedStoreKey = this.getPickupStoreKeyFromRouteStoreId(this.routePickupStoreId);
        if (routeSelectedStoreKey) {
          this.selectedPickupStoreKey = routeSelectedStoreKey;
          return;
        }
      }

      const hasSelectedStore = this.pickupStoreOptions
        .some((storeOption) => storeOption.key === this.selectedPickupStoreKey);

      if (!hasSelectedStore) {
        this.selectedPickupStoreKey = 'all';
      }
    },
    getProductGroupKey(item) {
      const product = item && item.product ? item.product : null;
      if (!product) return 'product-na';

      if (product.product_id !== undefined && product.product_id !== null && product.product_id !== '') {
        return `product-id-${product.product_id}`;
      }

      if (product.id !== undefined && product.id !== null && product.id !== '') {
        return `product-id-${product.id}`;
      }

      return `${product.name || 'N/A'}-${product.brand_name || 'N/A'}-${product.model || 'N/A'}`;
    },
    getDeliveryStoreName(clientDelivery) {
      if (!clientDelivery || !clientDelivery.store) return 'Sucursal no definida';

      const storeName = String(clientDelivery.store.name || '').trim();
      return storeName || 'Sucursal no definida';
    },
    getDeliveryStoreAddress(clientDelivery) {
      if (!clientDelivery || !clientDelivery.store) return '';

      return String(clientDelivery.store.address || '').trim();
    },
    getDeliveryGroupKey(clientDelivery) {
      const createdAt = clientDelivery && clientDelivery.created_at
        ? String(clientDelivery.created_at).trim()
        : '';
      const storeName = this.getDeliveryStoreName(clientDelivery).toLowerCase();
      const storeAddress = this.getDeliveryStoreAddress(clientDelivery).toLowerCase();

      return `${createdAt}|${storeName}|${storeAddress}`;
    },
  },
  computed: {
    addressLines() {
      return this.ticketData && this.ticketData.store
        ? this.splitAddress(this.ticketData.store.address)
        : [];
    },
    addressLine1() {
      return this.ticketData && this.ticketData.store.streetAddress
        ? this.ticketData.store.streetAddress
        : 'N/A';
    },
    addressLine2() {
      return this.ticketData && this.ticketData.store && this.ticketData.store.suburb
        ? this.ticketData.store.suburb
        : 'N/A';
    },
    addressLine3() {
      // Append postalCode, city, state, country
      if (!this.ticketData) return 'N/A';
      const postalCode = this.ticketData && this.ticketData.store ? this.ticketData.store.postalCode || 'N/A' : 'N/A';
      const city = this.ticketData && this.ticketData.store ? this.ticketData.store.city || 'N/A' : 'N/A';
      const state = this.ticketData && this.ticketData.store ? this.ticketData.store.state || 'N/A' : 'N/A';
      const country = this.ticketData && this.ticketData.store ? this.ticketData.store.country || 'N/A' : 'N/A';
      return `${postalCode}, ${city}, ${state}, ${country}`;
    },
    addressLine4() {
      return this.ticketData && this.ticketData.store && this.ticketData.store.phone
        ? `${this.ticketData.store.phone}`
        : 'N/A';
    },
    createdAt() {
      return this.ticketData && this.ticketData.created_at
        ? this.formatDate(this.ticketData.created_at)
        : 'N/A';
    },
    printedAt() {
      return this.ticketData && this.ticketData.response_datetime
        ? this.formatDate(this.ticketData.response_datetime)
        : this.formatDate(new Date());
    },
    foil() {
      return this.ticketData && this.ticketData.foil ? this.ticketData.foil : 'N/A';
    },
    clientName() {
      return this.ticketData && this.ticketData.client
        ? this.ticketData.client.name
        : 'Público en General';
    },
    clientId() {
      return this.ticketData && this.ticketData.client
        ? this.ticketData.client.id
        : 'N/A';
    },
    authorizedClientName() {
      return this.ticketData && this.ticketData.authorized_client_name
        ? this.ticketData.authorized_client_name
        : 'N/A';
    },
    authorizedClientContact() {
      return this.ticketData && this.ticketData.authorized_client_contact
        ? this.ticketData.authorized_client_contact
        : 'N/A';
    },
    authorizedClientEmail() {
      return this.ticketData && this.ticketData.authorized_client_email
        ? this.ticketData.authorized_client_email
        : 'N/A';
    },
    pickupStoreOptions() {
      if (!this.ticketData || !Array.isArray(this.ticketData.items)) return [];

      const options = [{
        key: 'all',
        label: 'Todas',
        store: null,
      }];

      const uniqueStores = this.ticketData.items.reduce((acc, item) => {
        const key = this.getPickupStoreKey(item.pickup_store);
        if (!key || acc[key]) return acc;

        acc[key] = {
          key,
          label: this.getPickupStoreLabel(item.pickup_store),
          store: item.pickup_store,
        };

        return acc;
      }, {});

      return options.concat(Object.values(uniqueStores));
    },
    selectedPickupStore() {
      if (this.selectedPickupStoreKey === 'all') return { key: 'all', label: 'Todas', store: null };

      return this.pickupStoreOptions
        .find((storeOption) => storeOption.key === this.selectedPickupStoreKey) || null;
    },
    showPickupStoreColumn() {
      return this.selectedPickupStoreKey === 'all';
    },
    routePickupStoreId() {
      const routeSucursalId = this.$route && this.$route.params ? this.$route.params.sucursal : null;
      const routeStoreId = this.$route && this.$route.params ? this.$route.params.storeId : null;
      const routePickupStoreId = this.$route && this.$route.params
        ? this.$route.params.pickupStoreId
        : null;
      const querySucursalId = this.$route && this.$route.query ? this.$route.query.sucursal : null;
      const queryStoreId = this.$route && this.$route.query ? this.$route.query.storeId : null;
      const queryPickupStoreId = this.$route && this.$route.query
        ? this.$route.query.pickupStoreId
        : null;

      return this.normalizeStoreId(
        routeSucursalId
        || routeStoreId
        || routePickupStoreId
        || querySucursalId
        || queryStoreId
        || queryPickupStoreId,
      );
    },
    hasRoutePickupStoreSelection() {
      return !!this.routePickupStoreId;
    },
    hasRouteAllSelection() {
      const routeMode = this.$route && this.$route.params ? this.$route.params.mode : null;
      const normalizedRouteMode = this.normalizeStoreId(routeMode);

      if (normalizedRouteMode && normalizedRouteMode.toLowerCase() === 'todas') {
        return true;
      }

      const routePath = this.$route && this.$route.path ? this.$route.path : '';
      return String(routePath).replace(/\/+$/, '').endsWith('/todas');
    },
    pickupStoreName() {
      if (this.selectedPickupStoreKey === 'all') return 'Todas';
      return this.selectedPickupStore ? this.selectedPickupStore.label : 'N/A';
    },
    filteredItemsByPickupStore() {
      if (!this.ticketData || !Array.isArray(this.ticketData.items)) return [];

      if (!this.selectedPickupStoreKey || this.selectedPickupStoreKey === 'all') return this.ticketData.items;

      return this.ticketData.items.filter(
        (item) => this.getPickupStoreKey(item.pickup_store) === this.selectedPickupStoreKey,
      );
    },
    requiresDocument() {
      console.log(this.ticketData);
      return this.ticketData.items != null && this.ticketData.items.length > 0;
    },
    hasPendingItems() {
      console.log(this.filteredItemsByPickupStore);
      return this.filteredItemsByPickupStore.some((item) => {
        const deliveryStatus = Number(item.delivery_status);
        return deliveryStatus === 2 || deliveryStatus === 3 || deliveryStatus === 4;
      });
    },
    groupedItems() {
      if (!this.filteredItemsByPickupStore.length) return [];

      const grouped = this.filteredItemsByPickupStore.reduce((acc, item) => {
        const key = this.getProductGroupKey(item);
        const deliveryStatus = Number(item.delivery_status);
        if (!acc[key]) {
          acc[key] = {
            key,
            product: item.product,
            quantity: 0,
            pending_quantity: 0,
            delivered_quantity: 0,
            pickupStoreLabels: [],
          };
        }
        acc[key].quantity += 1;

        const pickupStoreLabel = this.getPickupStoreLabel(item.pickup_store);
        if (pickupStoreLabel && pickupStoreLabel !== 'N/A' && !acc[key].pickupStoreLabels.includes(pickupStoreLabel)) {
          acc[key].pickupStoreLabels.push(pickupStoreLabel);
        }

        if (deliveryStatus === 2 || deliveryStatus === 3 || deliveryStatus === 4) {
          acc[key].pending_quantity += 1;
        }

        if (deliveryStatus === 5) {
          acc[key].delivered_quantity += 1;
        }

        return acc;
      }, {});

      return Object.values(grouped).map((group) => ({
        ...group,
        pickupStoreDisplay: group.pickupStoreLabels.join(', '),
      }));
    },
    deliveredGroupedItems() {
      if (!this.filteredItemsByPickupStore.length) return [];

      const grouped = this.filteredItemsByPickupStore.reduce((acc, item) => {
        const deliveryStatus = Number(item.delivery_status);
        if (deliveryStatus !== 5) return acc;

        const productKey = this.getProductGroupKey(item);
        if (!acc[productKey]) {
          acc[productKey] = {
            key: productKey,
            product: item.product,
            delivered_quantity: 0,
            deliveries_map: {},
          };
        }

        acc[productKey].delivered_quantity += 1;

        const deliveryInfo = item.client_delivery || null;
        const deliveryKey = this.getDeliveryGroupKey(deliveryInfo);

        if (!acc[productKey].deliveries_map[deliveryKey]) {
          acc[productKey].deliveries_map[deliveryKey] = {
            key: deliveryKey,
            created_at: deliveryInfo && deliveryInfo.created_at ? deliveryInfo.created_at : null,
            formatted_date: this.formatDate(
              deliveryInfo && deliveryInfo.created_at ? deliveryInfo.created_at : null,
            ),
            store_name: this.getDeliveryStoreName(deliveryInfo),
            quantity: 0,
          };
        }

        acc[productKey].deliveries_map[deliveryKey].quantity += 1;
        return acc;
      }, {});

      return Object.values(grouped).map((item) => {
        const deliveries = Object.values(item.deliveries_map).sort((a, b) => {
          const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
          return bTime - aTime;
        });

        return {
          key: item.key,
          product: item.product,
          delivered_quantity: item.delivered_quantity,
          deliveries,
        };
      });
    },
  },
  watch: {
    pickupStoreOptions: {
      immediate: true,
      handler() {
        this.ensureSelectedPickupStore();
      },
    },
    routePickupStoreId() {
      this.ensureSelectedPickupStore();
    },
  },
  beforeMount() {
    // Fetch the ticket data before the component is mounted
    this.loading = true;
    ClientDeliveryService.getDeliveryTicketData(this.$route.params.folio)
      .then((response) => {
        this.ticketData = response.data || this.defaultTicketData;
        this.ensureSelectedPickupStore();
      })
      .catch((error) => {
        console.error('Error al obtener los datos del ticket de entrega:', error);
      })
      .finally(() => {
        this.loading = false;
      });
  },
};
</script>

<style scoped>
/* ========================================================
   1. ESTILOS DE VISUALIZACIÓN EN PANTALLA (WEB)
   ======================================================== */
.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
}

.completion-state {
  width: 100%;
  max-width: 760px;
}

.completion-card {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  border-radius: 12px;
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border: 1px solid #a7f3d0;
  border-left: 4px solid #10b981;
  box-shadow: 0 10px 25px -12px rgba(15, 23, 42, 0.2);
}

.completion-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background-color: #dcfce7;
  color: #15803d;
  font-size: 1.3rem;
  font-weight: 700;
  flex-shrink: 0;
}

.completion-content h1 {
  margin: 0 0 0.5rem;
  font-size: 1.35rem;
  font-weight: 800;
  color: #0f172a;
}

.completion-content p {
  margin: 0 0 1rem;
  font-size: 0.95rem;
  line-height: 1.6;
  color: #475569;
}

.print-preview-wrapper {
  background-color: #e2e8f0;
  /* Fondo gris oscuro de la app */
  min-height: 100vh;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: 'Inter', 'Roboto', Arial, sans-serif;
  color: #1e293b;
}

.action-bar {
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.pickup-store-select {
  min-width: 300px;
  max-width: 480px;
  width: 100%;
}

.btn-print {
  background-color: #10b981;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transition: background-color 0.2s;
}

.btn-print:hover {
  background-color: #059669;
}

/* Formato exacto Carta (Letter) */
.hoja-carta {
  background: white;
  width: 8.5in;
  min-height: 11in;
  padding: 0.3in;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  position: relative;
  box-sizing: border-box;
}

/* ========================================================
   2. ESTILOS INTERNOS DEL DOCUMENTO (COMÚN PANTALLA E IMPRESIÓN)
   ======================================================== */
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: 2px solid #e2e8f0;
  padding-bottom: 1rem;
  margin-bottom: 1.5rem;
}

.brand-container {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.company-logo {
  width: 70px;
  height: 70px;
  object-fit: contain;
}

.brand-info {
  display: flex;
  flex-direction: column;
}

.logo-title {
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 4px 0;
  line-height: 1.1;
}

.company-data {
  margin: 1px 0;
  font-size: 0.85rem;
  line-height: 1.3;
}

.address-line {
  display: flex;
  flex-direction: column;
}

.address-line-part {
  display: block;
}

.address-line {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  max-width: 100%;
}

.text-muted {
  color: #64748b;
}

.folio-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.folio-box {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 12px 20px;
  border-radius: 6px;
  text-align: right;
  display: flex;
  align-items: center;
  gap: 15px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.folio-title {
  font-size: 0.75rem;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.5px;
}

.folio-number {
  font-size: 1.4rem;
  font-weight: 800;
  color: #0f172a;
  margin: 4px 0;
}

.badge-status {
  display: inline-block;
  background-color: #fef3c7;
  color: #b45309;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 99px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.badge-status.delivered {
  background-color: #d1fae5;
  color: #065f46;
}

.header-qr {
  width: 75px;
  height: 75px;
  background: white;
  border-radius: 4px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

/* Seguridad */
.security-section {
  margin-bottom: 2rem;
  width: 100%;
}

.qr-text {
  font-size: 0.7rem;
  color: #64748b;
  margin-top: 8px;
  line-height: 1.2;
  text-align: end;
}

.alert-box {
  display: flex;
  gap: 12px;
  background-color: #f0f9ff;
  border-left: 4px solid #0ea5e9;
  padding: 16px;
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.alert-icon {
  font-size: 1.5rem;
}

.alert-text {
  font-size: 0.85rem;
  line-height: 1.5;
  color: #0c4a6e;
}

/* Grid de Datos */
.data-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

.data-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #334155;
  margin-bottom: 10px;
  text-transform: uppercase;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 4px;
}

.data-row {
  font-size: 0.85rem;
  margin: 6px 0;
}

.data-row span {
  font-weight: 600;
  color: #64748b;
  display: inline-block;
  width: 80px;
}

.data-row.highlight span {
  color: #0f172a;
}

/* Tabla */
.items-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #334155;
  margin: 0 0 10px 0;
  text-transform: uppercase;
}

.items-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 2rem;
}
.items-table th {
  background-color: #1e293b;
  color: white;
  text-align: left;
  padding: 10px 12px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.items-table th.th-compact {
  font-size: 0.72rem;
  padding: 10px 8px;
  white-space: nowrap;
}

.items-table td {
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.85rem;
}

.items-table .text-center {
  text-align: center;
}

.font-mono {
  font-family: monospace;
  font-size: 0.9rem;
}

.font-medium {
  font-weight: 600;
}

.sku-subtitle {
  font-size: 0.75rem;
  margin-top: 2px;
}

.product-meta {
  font-size: 0.74rem;
  margin-top: 2px;
  line-height: 1.25;
}

.sku-cell {
  font-size: 0.76rem;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.item-qty {
  font-size: 1.1rem;
  font-weight: 800;
  color: #0f172a;
}

.delivered-product-row td {
  border-bottom: none;
}

.delivery-detail-row td {
  font-size: 0.78rem;
  padding-top: 2px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e2e8f0;
}

.delivery-detail-text {
  color: #334155;
  font-style: italic;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.delivery-qty {
  font-weight: 700;
  color: #0f172a;
}

.items-legend {
  margin-top: -1.2rem;
  margin-bottom: 1.4rem;
  font-size: 0.72rem;
  line-height: 1.35;
  text-align: right;
}

/* Footer */
.legal-footer {
  margin-top: auto;
  break-inside: avoid;
  page-break-inside: avoid;
}

.legal-text {
  font-size: 0.75rem;
  color: #64748b;
  text-align: justify;
  line-height: 1.4;
  margin-bottom: 3rem;
}

.signatures-area {
  display: flex;
  justify-content: space-between;
  padding: 0 1rem;
}
.signature-line {
  width: 40%;
  text-align: center;
}
.signature-line .line {
  border-top: 1px solid #000;
  margin-bottom: 8px;
  margin-top: 50px;
}

.signature-title {
  font-size: 0.85rem;
  font-weight: 700;
  margin: 0;
}

.signature-subtitle {
  font-size: 0.75rem;
  color: #64748b;
  margin: 2px 0 0 0;
}

.document-footer {
  font-size: 0.7rem;
  color: #64748b;
  text-align: end;
  margin-top: 2rem;
}

/* ========================================================
   3. ESTILOS ESTRICTOS DE IMPRESIÓN (@media print)
   ======================================================== */
@media print {

  /* Oculta los controles web */
  .no-print,
  .btn-print {
    display: none !important;
  }

  /* Quita los márgenes del body web y fondo oscuro */
  body,
  html {
    background-color: white !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .print-preview-wrapper {
    background-color: white !important;
    padding: 0 !important;
  }

  /* La hoja pierde sus sombras decorativas, asume los límites del papel físico */
  .hoja-carta {
    width: 100% !important;
    height: auto !important;
    min-height: auto !important;
    margin: 0 !important;
    padding: 0.2in !important;
    /* El padding se maneja con @page */
    box-shadow: none !important;
    border: none !important;
  }

  /* Define los márgenes nativos de la impresora */
  @page {
    size: letter;
    margin: .5cm .5cm;
  }

  /* Evita que la sección de firmas se corte entre páginas */
  .legal-footer {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  /* Repite el footer en cada hoja impresa fijándolo al pie de página */
  .document-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    margin: 0;
    padding: 0.2in;
  }
}

@media (min-width: 900px) {
  .pickup-store-select {
    width: 420px;
  }
}
</style>
<!-- BLOQUE DE ESTILO GLOBAL SOLO PARA IMPRESIÓN -->

<style>
@media print {
  /* Oculta la barra superior de Vuetify en toda la app al imprimir */
  header.v-toolbar,
  .v-app-bar {
    display: none !important;
  }
}
</style>
